import { Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlayerRecordFilled, IconSparkles } from '@tabler/icons-react';
import { useSetAtom } from 'jotai';
import { erroredOperationAtom } from '~/atoms/no-code-atoms';
import { useDeleteSteps } from '~/hooks/mutations/steps/useDeleteSteps';
import { useStartRecorder } from '~/hooks/vm/useStartRecorder';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';

export default function NoActionsPanel({
  testContentId,
  step,
  executing,
  readOnly,
}: {
  testContentId: string;
  step: SelectedStep;
  executing: boolean;
  readOnly: boolean;
}) {
  const setErroredOperation = useSetAtom(erroredOperationAtom);
  const startRecorder = useStartRecorder({ testContentId });

  const { mutateAsync: deleteSteps } = useDeleteSteps({ testContentId });

  const trpcUtils = api.useUtils();

  const { mutateAsync: updateStep } = api.testStep.update.useMutation({
    onSuccess: () => {
      notifications.show({
        message: 'Step mode updated to AI Dynamic',
      });
    },
    onError: () => {
      notifications.show({
        message: 'Failed to update step mode',
        color: 'red',
      });
    },
    onSettled: async () => {
      await Promise.allSettled([trpcUtils.testContent.get.invalidate({ id: testContentId })]);
    },
  });

  return (
    <div className="flex flex-col items-start gap-2 pb-3">
      <div className="text-sm text-gray-600 dark:text-gray-400">No actions</div>
      {!executing && !readOnly && (
        <div className="flex gap-2">
          <Button
            disabled={step.mode === 'AI_DYNAMIC' || step.mode === 'AUTH'}
            onClick={() => {
              setErroredOperation(undefined);
              startRecorder({
                executionType: 'record',
                location: {
                  type: 'step-id',
                  stepId: step.id,
                  order: step.stepOrder,
                },
              });
            }}
            size="compact-xs"
            variant="default"
            leftSection={<IconPlayerRecordFilled size={8} />}
          >
            Record
          </Button>
          <Button
            size="compact-xs"
            variant="default"
            leftSection={<IconSparkles size={12} />}
            disabled={step.mode === 'AI_DYNAMIC' || step.mode === 'AUTH'}
            onClick={async () => {
              await updateStep({ stepId: step.id, data: { mode: 'AI_DYNAMIC' } });
            }}
          >
            Add with AI
          </Button>
          <Button
            disabled={step.mode === 'AI_DYNAMIC' || step.mode === 'AUTH'}
            onClick={() => {
              modals.openConfirmModal({
                title: 'Please confirm',
                children: <span>This will permanently delete the step.</span>,
                labels: { confirm: `Delete Step`, cancel: 'Cancel' },
                confirmProps: {
                  color: 'red',
                },
                centered: true,
                onConfirm: async () => {
                  await deleteSteps({ stepIds: [step.id] });
                },
              });
            }}
            size="compact-xs"
            variant="default"
          >
            Delete
          </Button>
        </div>
      )}
    </div>
  );
}
